import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetMeApi } from 'eds-common/api/v2';
import { type MetricTableType } from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import { useMetricsTableV2Api } from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import { buildOrderBy } from '@data/utils/build-order-by';
import { useTableControls } from '@data/hooks/use-report-tables/use-table-controls/use-table-controls';
import { flattenGroupedColumns } from '@data/utils/flatten-grouped-columns';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { Drilldown } from '@data/main';
import { useResetTableOnFilterChange } from '@data/hooks/use-report-tables/use-reset-table-on-filter-change/use-reset-table-on-filter-change';
import { exportTableToCSV } from '@data/utils/export-table-to-csv';
import formatDate from '@data/utils/format-date';
import { EXPORT_ERROR_MESSAGE } from '@data/components/basic/Table/types';
import { getEdApps } from '@data/utils/get-ed-apps';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface UseAssessmentReportTableProps {
  metricType: MetricTableType;
  defaultSortColumn: string;
  /** When provided, navigates to this path with `?classId=<id>` on drilldown.
   *  Only active at the AllClasses drilldown level. */
  drilldownPath?: string;
}

// ── Hooks ───────────────────────────────────────────────────────────────────────

function useExportToCSVHandler(
  refetchExportData: ReturnType<typeof useMetricsTableV2Api>['refetch'],
  reportPath: string | undefined,
  edApp: string | undefined
) {
  return useCallback(async () => {
    const { data: exportData, error } = await refetchExportData();
    if (error) throw error;
    if (!exportData) throw new Error(EXPORT_ERROR_MESSAGE);

    const { columns: exportColumns, columnGroups: columnPrefixes } =
      flattenGroupedColumns(exportData.columns);

    exportTableToCSV(
      exportColumns,
      exportData.rows,
      `${reportPath ?? 'report'}_${edApp}_${formatDate(new Date())}.csv`,
      columnPrefixes
    );
  }, [refetchExportData, edApp, reportPath]);
}

function useDrilldownHandler(
  drilldownPath: string | undefined,
  dateParams: string,
  navigate: ReturnType<typeof useNavigate>
) {
  return useCallback(
    (id: string | number) => {
      // Carry the date range on drill-down only when a non-default range is
      // active — it is mirrored on the URL as `from`/`to` (mirrors the overview
      // report). On the default "Current school year" the URL has no date
      // params, so the drill-down opens on the school-year default instead of
      // flipping the date dropdown to "Custom range". Returning to the
      // param-less base report resets to the school year via ReportLayout.
      const suffix = dateParams ? `&${dateParams}` : '';
      navigate(`${drilldownPath ?? ''}?classId=${id}${suffix}`);
    },
    [navigate, drilldownPath, dateParams]
  );
}

export function useAssessmentReportTable({
  metricType,
  defaultSortColumn,
  drilldownPath,
}: UseAssessmentReportTableProps) {
  const navigate = useNavigate();
  const {
    dateRange,
    drilldown,
    productId: edApp,
    reportPath,
  } = useReportOutletContext();
  const { data: user } = useGetMeApi();
  const [searchParams] = useSearchParams();
  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');
  // Only a non-default range is mirrored on the URL as `from`/`to`; forward it
  // verbatim on drill-down and carry nothing on the school-year default.
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const dateParams =
    fromParam && toParam ? `from=${fromParam}&to=${toParam}` : '';

  const showTable =
    drilldown === Drilldown.AllClasses || drilldown === Drilldown.Class;

  const { tableProps, searchTerms, userSorted, reset } = useTableControls({
    defaultSortColumn,
  });

  useResetTableOnFilterChange(
    drilldown,
    classIds.join(','),
    studentIds.join(','),
    reset
  );

  const sharedMetricsArgs = {
    orgId: user?.currentOrg?.orgId ?? '',
    pageSize: tableProps.pageSize,
    metricType,
    teacherIds: [user?.userId ?? ''],
    startDate: dateRange.from,
    endDate: dateRange.to,
    edApps: getEdApps(edApp),
    classIds: classIds.length > 0 ? classIds : null,
    studentIds: studentIds.length > 0 ? studentIds : null,
    ...(searchTerms ? { searchTerms } : {}),
    ...buildOrderBy(
      tableProps.sortColumn,
      tableProps.sortDirection,
      searchTerms,
      userSorted
    ),
  };

  const {
    data: tableData,
    loading: tableMetricsLoading,
    error: tableMetricsError,
  } = useMetricsTableV2Api({
    ...sharedMetricsArgs,
    page: tableProps.page,
    classIds: classIds.length > 0 ? classIds : null,
    studentIds: studentIds.length > 0 ? studentIds : null,
    enabled:
      showTable && !!user?.userId && !!user?.currentOrg?.orgId && !!edApp,
  });

  const { refetch: refetchExportData } = useMetricsTableV2Api({
    ...sharedMetricsArgs,
    page: 1,
    fetchAllRecords: true,
    enabled: false,
  });

  const { columns: flatColumns, columnGroups } = useMemo(
    () => flattenGroupedColumns(tableData?.columns ?? []),
    [tableData]
  );

  const handleExportToCSV = useExportToCSVHandler(
    refetchExportData,
    reportPath,
    edApp
  );

  const handleDrilldown = useDrilldownHandler(
    drilldownPath,
    dateParams,
    navigate
  );

  return {
    showTable,
    tableProps,
    tableData,
    tableMetricsLoading,
    flatColumns,
    columnGroups,
    totalCount: tableData?.totalRecords ?? 0,
    handleExportToCSV,
    handleDrilldown:
      drilldownPath && drilldown === Drilldown.AllClasses
        ? handleDrilldown
        : undefined,
    drilldown,
    tableMetricsError,
  };
}
